import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  Upload,
  Trash2,
  Loader2,
  FileText,
  Building2,
  Tag,
  Search,
  Layers,
  AlertCircle,
  Sparkles,
  RefreshCw,
} from "lucide-react";
import { toast } from "sonner";

import TopNav from "@/components/TopNav";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  kblList,
  kblUpload,
  kblDelete,
  kblCompanies,
  kblSupported,
  kblSearch,
  kblReindex,
  kblStats,
} from "@/lib/api";

const EXT_LABELS = {
  pdf: "PDF",
  docx: "Word",
  pptx: "PowerPoint",
  xlsx: "Excel",
  xls: "Excel",
  txt: "TXT",
  md: "Markdown",
  csv: "CSV",
};

export default function KblLibrary() {
  const { user } = useAuth();
  const [companies, setCompanies] = useState([]);
  const [supported, setSupported] = useState({ extensions: [], max_file_bytes: 0 });
  const [company, setCompany] = useState(() => new URLSearchParams(window.location.search).get("empresa") || "");
  const [department, setDepartment] = useState("__all__");
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  // Upload form state
  const [upCompany, setUpCompany] = useState("");
  const [upDept, setUpDept] = useState("");
  const [upTags, setUpTags] = useState("");

  // RAG state
  const [stats, setStats] = useState(null);
  const [reindexing, setReindexing] = useState(false);
  const [ragQuery, setRagQuery] = useState("");
  const [ragResults, setRagResults] = useState([]);
  const [ragSearching, setRagSearching] = useState(false);

  const loadStats = async () => {
    try {
      const d = await kblStats(company || undefined);
      setStats(d);
    } catch {
      setStats(null);
    }
  };

  const loadCompanies = async () => {
    try {
      const d = await kblCompanies();
      setCompanies(d.companies || []);
      // Fix 7 (B7): the filter works by slug (a company may have a custom slug); ?empresa= may
      // still carry the name.
      const byName = (d.companies || []).find((x) => x.company === company);
      if (byName) setCompany(byName.company_slug);
      if (!company && d.companies?.length) {
        setCompany(d.companies[0].company_slug);
      }
    } catch {
      // empty
    }
  };

  const load = async () => {
    try {
      setLoading(true);
      const d = await kblList(
        company || undefined,
        department && department !== "__all__" ? department : undefined,
      );
      setDocs(d.documents || []);
    } catch {
      toast.error("Error cargando KBL.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    kblSupported().then(setSupported).catch(() => {});
    loadCompanies();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    load();
    loadStats();
    setRagResults([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [company, department]);

  const onReindex = async () => {
    try {
      setReindexing(true);
      const r = await kblReindex(company || undefined);
      toast.success(`Reindexado: ${r.processed} documento(s) · ${r.chunks_indexed} fragmento(s)` +
        (r.skipped_up_to_date ? ` · ${r.skipped_up_to_date} ya al día` : "") +
        (r.errors?.length ? ` · ${r.errors.length} con error` : ""));
      await load();
      await loadStats();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al reindexar.");
    } finally {
      setReindexing(false);
    }
  };

  const onRagSearch = async (e) => {
    e?.preventDefault?.();
    if (!company) return toast.error("Seleccione una empresa para buscar.");
    if (!ragQuery.trim()) return toast.error("Escriba una consulta.");
    try {
      setRagSearching(true);
      const r = await kblSearch({
        company,
        department: department && department !== "__all__" ? department : undefined,
        query: ragQuery.trim(),
        top_k: 5,
      });
      setRagResults(r.results || []);
      if (!(r.results || []).length) toast.message("Sin coincidencias semánticas.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al buscar.");
    } finally {
      setRagSearching(false);
    }
  };

  const onUpload = async (e) => {
    e.preventDefault();
    const file = fileRef.current?.files?.[0];
    if (!file) return toast.error("Seleccione un archivo.");
    if (!upCompany || !upDept.trim()) return toast.error("Empresa y departamento son obligatorios.");
    try {
      setUploading(true);
      // upCompany = slug of a registered company or of an existing library (no free text)
      const up = await kblUpload({ file, company: upCompany, department: upDept.trim(), tags: upTags.trim() });
      // Fix 10: 0 fragments (scanned PDF, unreadable file...) is a warning, never a silent success
      if (up?.warning || !up?.rag_chunks) {
        toast.warning(`"${file.name}" se guardó, pero ${up?.warning || "no tiene texto extraíble (0 fragmentos): los agentes no lo usarán."}`, { duration: 12000 });
      } else {
        toast.success(`"${file.name}" cargado en ${upDept} · ${up.rag_chunks} fragmento(s).`);
      }
      // Always switch to the just-uploaded company so the user sees the new row immediately
      setCompany(upCompany);
      setDepartment("__all__");
      setUpTags("");
      if (fileRef.current) fileRef.current.value = "";
      await loadCompanies();
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al subir.");
    } finally {
      setUploading(false);
    }
  };

  const onDelete = async (d) => {
    if (!window.confirm(`¿Eliminar "${d.filename}" del KBL?`)) return;
    try {
      await kblDelete(d.id);
      toast.success("Documento eliminado.");
      await load();
      await loadCompanies();
    } catch {
      toast.error("Error al eliminar.");
    }
  };

  const departmentsForCompany = useMemo(() => {
    const c = companies.find((x) => x.company_slug === company || x.company === company);
    return (c?.departments || []).sort();
  }, [company, companies]);

  const totalChars = docs.reduce((s, d) => s + (d.chars || 0), 0);

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white text-slate-900" data-testid="kbl-page">
      <TopNav
        right={user?.role === "admin" && (
          <Link to="/admin/facturacion">
            <Button variant="outline" className="rounded-sm border-slate-300 text-[11px] uppercase tracking-[0.2em] text-slate-700 hover:bg-slate-100">
              Facturación
            </Button>
          </Link>
        )}
      />
      <main className="mx-auto max-w-6xl px-6 py-10 lg:px-10">
        <p className="eyebrow text-accent-blue">Administración</p>
        <h1 className="mt-3 font-display text-4xl font-medium text-slate-900 sm:text-5xl">
          Knowledge Base Library
        </h1>
        <p className="mt-3 max-w-2xl text-sm text-slate-600">
          Suba documentos (PDF, Word, PowerPoint, Excel, TXT) por <strong>empresa</strong> y
          etiquetados con un <strong>departamento</strong>. Al generar los Agentes IA, cada uno se
          auto-entrena con los documentos etiquetados para su propio departamento.
        </p>

        {/* Upload form */}
        <section className="mt-10 border border-slate-200 bg-white p-6" data-testid="kbl-upload-card">
          <p className="eyebrow">Subir documento</p>
          <form onSubmit={onUpload} className="mt-5 grid grid-cols-1 gap-4 lg:grid-cols-12">
            <Field className="lg:col-span-4" label="Empresa">
              <Select value={upCompany} onValueChange={setUpCompany}>
                <SelectTrigger className="rounded-sm border-slate-300" data-testid="kbl-up-company">
                  <SelectValue placeholder="Seleccione un cliente o biblioteca" />
                </SelectTrigger>
                <SelectContent>
                  {companies.map((c) => (
                    <SelectItem key={c.company_slug} value={c.company_slug}>
                      {c.company}{c.count ? ` (${c.count})` : " (sin documentos)"}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field className="lg:col-span-4" label="Departamento (tag obligatorio)">
              <Input
                value={upDept}
                onChange={(e) => setUpDept(e.target.value)}
                placeholder="Ej.: Atención al Cliente · «General» = toda la empresa"
                className="rounded-sm border-slate-300"
                data-testid="kbl-up-dept"
                title="Los documentos con departamento «General» los usan todos los agentes de la empresa. Mayúsculas y tildes no importan."
              />
            </Field>
            <Field className="lg:col-span-4" label="Etiquetas adicionales (coma-separadas)">
              <Input
                value={upTags}
                onChange={(e) => setUpTags(e.target.value)}
                placeholder="politica, servicio, sla"
                className="rounded-sm border-slate-300"
                data-testid="kbl-up-tags"
              />
            </Field>
            <Field className="lg:col-span-8" label={`Archivo · soportados: ${(supported.extensions || []).map((e) => "." + e).join(" · ")}`}>
              <Input
                ref={fileRef}
                type="file"
                accept={(supported.extensions || []).map((e) => "." + e).join(",")}
                className="rounded-sm border-slate-300"
                data-testid="kbl-up-file"
              />
            </Field>
            <div className="lg:col-span-4 flex items-end">
              <Button
                type="submit"
                disabled={uploading}
                className="w-full rounded-sm bg-slate-900 text-[11px] uppercase tracking-[0.2em] text-white hover:bg-slate-800"
                data-testid="kbl-upload-btn"
              >
                {uploading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />}
                Subir al KBL
              </Button>
            </div>
          </form>
          <p className="mt-4 text-[11px] uppercase tracking-[0.18em] text-amber-700 flex items-center gap-1.5">
            <AlertCircle className="h-3 w-3" /> Tamaño máximo {Math.round(supported.max_file_bytes / 1024 / 1024)} MB por archivo
          </p>
        </section>

        {/* RAG status + semantic search tester */}
        <section className="mt-10 border border-teal-700/30 bg-teal-50/60 p-6" data-testid="kbl-rag-card">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <p className="eyebrow text-accent-blue flex items-center gap-1.5"><Sparkles className="h-3 w-3" /> RAG · Búsqueda semántica</p>
              <h2 className="mt-2 font-display text-2xl font-medium text-slate-900">
                Knowledge Base indexada
              </h2>
              <p className="mt-1 text-xs text-slate-600">
                Modelo de embeddings: <span className="font-mono">{stats?.model || "—"}</span>
                {stats?.embedding_dim ? <> · dimensión <span className="font-mono">{stats.embedding_dim}</span></> : null}
                {" · "}
                <span className="font-mono">{(stats?.total_chunks ?? 0).toLocaleString("es-419")}</span> fragmento(s) indexado(s)
                {company ? <> para <span className="font-semibold">{company}</span></> : null}.
              </p>
            </div>
            <Button
              onClick={onReindex}
              disabled={reindexing}
              variant="outline"
              className="rounded-sm border-teal-700/40 text-[11px] uppercase tracking-[0.2em] text-teal-800 hover:bg-teal-100"
              data-testid="kbl-reindex-btn"
            >
              {reindexing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}
              {reindexing ? "Reindexando…" : "Reindexar embeddings"}
            </Button>
          </div>

          <form onSubmit={onRagSearch} className="mt-5 flex flex-col gap-3 sm:flex-row">
            <Input
              value={ragQuery}
              onChange={(e) => setRagQuery(e.target.value)}
              placeholder={company
                ? `Pruebe la búsqueda semántica en «${company}»${department && department !== "__all__" ? " / " + department : ""}…`
                : "Seleccione primero una empresa abajo para probar la búsqueda…"}
              className="rounded-sm border-slate-300 bg-white"
              data-testid="kbl-rag-query"
              disabled={!company}
            />
            <Button
              type="submit"
              disabled={ragSearching || !company}
              className="rounded-sm bg-accent-blue text-[11px] uppercase tracking-[0.2em] text-white hover:bg-accent-blue"
              data-testid="kbl-rag-search-btn"
            >
              {ragSearching ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Search className="mr-2 h-4 w-4" />}
              Buscar
            </Button>
          </form>

          {ragResults.length > 0 && (
            <div className="mt-5 space-y-3" data-testid="kbl-rag-results">
              {ragResults.map((r, i) => (
                <div key={`${r.doc_id}-${r.chunk_index}`} className="border border-slate-200 bg-white p-4">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <span className="font-display text-sm text-slate-900">[{i + 1}] {r.filename}</span>
                      <span className="border border-teal-700/40 bg-teal-50 px-2 py-0.5 text-[10px] uppercase tracking-[0.1em] text-teal-800">
                        {r.department}
                      </span>
                      <span className="font-mono text-[10px] uppercase tracking-[0.12em] text-slate-500">
                        frag #{r.chunk_index}
                      </span>
                    </div>
                    <span className="font-mono text-[11px] text-teal-800">
                      score {r.score.toFixed(3)}
                    </span>
                  </div>
                  <p className="mt-2 whitespace-pre-wrap text-xs text-slate-700">
                    {r.text}
                  </p>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Filters + List */}
        <section className="mt-10">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <p className="eyebrow">Biblioteca</p>
              <h2 className="mt-2 font-display text-2xl font-medium text-slate-900">
                Documentos cargados
              </h2>
              <p className="mt-1 text-[11px] uppercase tracking-[0.18em] text-slate-500">
                {docs.length} documento(s) · {totalChars.toLocaleString("es-419")} caracteres extraídos
              </p>
            </div>
            <div className="flex flex-wrap items-end gap-3">
              <div>
                <label className="text-[10px] uppercase tracking-[0.22em] text-slate-500 flex items-center gap-1"><Building2 className="h-3 w-3" /> Empresa</label>
                <Select value={company || "__all__"} onValueChange={(v) => { setCompany(v === "__all__" ? "" : v); setDepartment("__all__"); }}>
                  <SelectTrigger className="mt-2 h-10 w-[220px] rounded-sm border-slate-300 bg-white text-xs" data-testid="filter-company">
                    <SelectValue placeholder="Todas" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">Todas</SelectItem>
                    {companies.map((c) => (
                      <SelectItem key={c.company_slug} value={c.company_slug}>
                        {c.company} ({c.count})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-[0.22em] text-slate-500 flex items-center gap-1"><Layers className="h-3 w-3" /> Departamento</label>
                <Select value={department} onValueChange={setDepartment} disabled={!company}>
                  <SelectTrigger className="mt-2 h-10 w-[220px] rounded-sm border-slate-300 bg-white text-xs" data-testid="filter-department">
                    <SelectValue placeholder="Todos" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">Todos</SelectItem>
                    {departmentsForCompany.map((d) => (
                      <SelectItem key={d} value={d}>{d}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {loading ? (
            <div className="mt-10 flex items-center gap-3 text-slate-500">
              <Loader2 className="h-5 w-5 animate-spin" /> Cargando…
            </div>
          ) : docs.length === 0 ? (
            <div className="mt-8 border border-dashed border-slate-300 bg-white p-10 text-center">
              <Search className="mx-auto h-8 w-8 text-slate-300" />
              <p className="mt-4 font-display text-lg text-slate-900">Sin documentos</p>
              <p className="mt-2 text-sm text-slate-600">
                Cargue el primer documento usando el formulario de arriba.
              </p>
            </div>
          ) : (
            <div className="mt-6 overflow-x-auto" data-testid="kbl-list">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200 text-left text-[10px] uppercase tracking-[0.18em] text-slate-500">
                    <th className="px-3 py-2">Archivo</th>
                    <th className="px-3 py-2">Empresa</th>
                    <th className="px-3 py-2">Departamento</th>
                    <th className="px-3 py-2">Etiquetas</th>
                    <th className="px-3 py-2 text-right">Tamaño</th>
                    <th className="px-3 py-2 text-right">Texto</th>
                    <th className="px-3 py-2 text-right">RAG</th>
                    <th className="px-3 py-2 text-right">Cargado</th>
                    <th className="px-3 py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {docs.map((d) => (
                    <tr key={d.id} className="border-b border-slate-100" data-testid={`kbl-row-${d.id}`}>
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-2">
                          <FileText className="h-3.5 w-3.5 text-slate-400" />
                          <div>
                            <div className="font-display text-sm text-slate-900">{d.filename}</div>
                            <div className="font-mono text-[10px] uppercase tracking-[0.12em] text-slate-500">
                              {EXT_LABELS[d.ext] || d.ext}
                              {d.extraction_error && <span className="ml-2 text-amber-700">· error extracción</span>}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-3 py-2 text-slate-700">{d.company}</td>
                      <td className="px-3 py-2">
                        <span className="inline-flex items-center gap-1 border border-teal-700/40 bg-teal-50 px-2 py-0.5 text-[10px] uppercase tracking-[0.1em] text-teal-800">
                          {d.department}
                        </span>
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex flex-wrap gap-1">
                          {(d.tags || []).filter((t) => t !== d.department).slice(0, 6).map((t) => (
                            <span key={t} className="inline-flex items-center gap-1 border border-slate-300 px-1.5 py-0.5 text-[10px] text-slate-700">
                              <Tag className="h-2.5 w-2.5" /> {t}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className="px-3 py-2 text-right font-mono text-[11px] text-slate-700">
                        {Math.round((d.size_bytes || 0) / 1024)} KB
                      </td>
                      <td className="px-3 py-2 text-right font-mono text-[11px] text-slate-700">
                        {(d.chars || 0).toLocaleString("es-419")}
                      </td>
                      <td className="px-3 py-2 text-right">
                        {d.rag_chunks ? (
                          <span className="inline-flex items-center gap-1 border border-teal-700/40 bg-teal-50 px-2 py-0.5 font-mono text-[10px] uppercase tracking-[0.1em] text-teal-800" data-testid={`kbl-rag-status-${d.id}`}
                            title={d.rag_outdated ? "Fragmentado con una versión anterior: use «Reindexar embeddings»." : undefined}>
                            <Sparkles className="h-2.5 w-2.5" /> {d.rag_chunks} frag.{d.rag_outdated ? " · reindexar" : ""}
                          </span>
                        ) : (
                          <span className="inline-flex max-w-[220px] items-center gap-1 border border-amber-600/50 bg-amber-50 px-2 py-0.5 text-left font-mono text-[10px] tracking-[0.02em] text-amber-800" data-testid={`kbl-rag-status-${d.id}`}
                            title={d.rag_warning || d.rag_error || d.extraction_error || "Sin texto extraíble, 0 fragmentos"}>
                            ⚠ {d.ext === "pdf" ? "PDF sin texto extraíble" : "Sin texto extraíble"}, 0 fragmentos
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right font-mono text-[10px] text-slate-500">
                        {new Date(d.uploaded_at).toLocaleString("es-419")}
                      </td>
                      <td className="px-3 py-2 text-right">
                        <button
                          onClick={() => onDelete(d)}
                          className="rounded-sm p-2 text-rose-500 hover:bg-rose-50 hover:text-rose-700"
                          data-testid={`kbl-delete-${d.id}`}
                          title="Eliminar"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        <p className="mt-12 text-[11px] uppercase tracking-[0.18em] text-accent-blue">
          ✶ RAG activo: cada consulta a un agente recupera automáticamente los fragmentos
          más relevantes de los documentos etiquetados con su departamento (sin enviar todo el texto al modelo).
        </p>
      </main>
    </div>
  );
}

function Field({ label, children, className = "" }) {
  return (
    <div className={className}>
      <label className="text-[10px] uppercase tracking-[0.22em] text-slate-700">{label}</label>
      <div className="mt-2">{children}</div>
    </div>
  );
}
